import { Fragment, PureComponent, useMemo } from 'react';
import {
  Route,
  Redirect,
  RouteComponentProps,
  useHistory,
} from 'react-router-dom';
import ThemeContext from '../../../shared/util/ThemeContext';
import SiteLandingPage from 'builder/scenes/SiteBuilder/SiteLandingPage/SiteLandingPageContainer';
import Preview from 'builder/scenes/Preview';
import Loading from 'builder/scenes/Loading';
import SiteEditContext from 'shared/util/SiteEditContext';
import env from 'builder/config';
import Feedback from 'shared/components/Feedback/Feedback';
import { User } from 'shared/state/misc/oidc';
import SearchWidgetContainer from 'shared/widgets/Search/SearchResultsContainer';
import PageLayout from 'client/components/PageLayout';
import { processWidgetData } from 'client/components/PageLayout/utils';
import { widgetTypes } from 'builder/util/constants';

import { ThemeProvider } from 'styled-components';
import { resolveLegacyThemeFromSite } from 'builder/util/resolveTheme';

export interface SiteRouterProps extends RouteComponentProps<any> {
  siteId: number;
  site: Site;
  siteMeta: StateMeta;
  fetchPages: () => void;
  isEditingTemplate: boolean;
  onUpdateConfig?: (
    config: any,
    widgetId: string,
    defaultWidget: boolean
  ) => void;
  initiateLogout?: () => void;
  pages: Array<Page>;
  user?: User;
}

// Memoized view for search results to avoid recomputation of widgets on every render.
const SearchResultsView: React.FC<{
  site: Site;
  pages: Array<Page>;
  user?: User;
  onUpdateConfig?: (
    config: any,
    widgetId: string,
    defaultWidget: boolean
  ) => void;
  initiateLogout?: () => void;
  current: Page['current'];
  qpPageId: number;
}> = ({
  site,
  pages,
  user,
  onUpdateConfig,
  initiateLogout,
  current,
  qpPageId,
}) => {
  const history = useHistory();

  const widgets = useMemo(
    () =>
      processWidgetData(
        current,
        onUpdateConfig,
        user,
        pages,
        site.siteId,
        site,
        initiateLogout,
        history
      ),
    [current, onUpdateConfig, user, pages, site.siteId, site, initiateLogout]
  );

  const searchWidgetState = useMemo(
    () => widgets.find(widget => widget.type === widgetTypes.Search),
    [widgets]
  );

  return (
    <PageLayout initialWidgets={widgets} currentPage={current} site={site}>
      <SearchWidgetContainer
        site={site}
        currentPage={current}
        searchWidgetState={searchWidgetState}
        pageId={qpPageId}
      />
    </PageLayout>
  );
};

class SiteRouter extends PureComponent<SiteRouterProps> {
  componentDidMount() {
    const { fetchPages, isEditingTemplate } = this.props;
    if (!isEditingTemplate) {
      fetchPages();
    }
  }

  render() {
    const {
      match: { path },
      siteId,
      siteMeta,
      site,
      user,
      history,
      onUpdateConfig,
      initiateLogout,
      pages,
    } = this.props;

    // Only check successful (not !pending) so that a pages re-fetch
    // (FETCH_PAGES_PENDING while siteMeta.successful=true on revisit) does
    // NOT temporarily unmount SiteLandingPage. Unmount + remount caused a
    // second fetchPage call for the current page.
    if (siteMeta.successful) {
      const appliedTheme: Theme = resolveLegacyThemeFromSite(site.theme);

      return (
        <Fragment>
          <ThemeProvider theme={appliedTheme}>
            {/* Keep ThemeContext unchanged so existing consumers keep working */}
            <ThemeContext.Provider value={{ theme: site.theme }}>
              <SiteEditContext.Provider
                value={{
                  siteEditPermission:
                    !env.permissionsEnabled || site.access.edit,
                }}
              >
                <Route
                  path={path}
                  exact
                  render={() => (
                    <SiteLandingPage
                      siteId={siteId}
                      pageId={site.horizonhomepageId}
                      parentWidgetId={'root'}
                      history={history}
                    />
                  )}
                />
                <Route
                  path="/sites/:siteId/preview/"
                  render={({ match: { url: basePath } }) => {
                    return <Preview basePath={basePath} siteId={siteId} />;
                  }}
                />
                <Route
                  path="/sites/:siteId/pages/:pageId"
                  render={({
                    match: {
                      params: { pageId: rawPageId },
                    },
                  }) => {
                    const pageId = parseInt(rawPageId, 10);

                    if (isNaN(pageId)) {
                      return <Redirect to={path} />;
                    }
                    return (
                      <>
                        <SiteLandingPage
                          siteId={siteId}
                          pageId={pageId}
                          parentWidgetId={'root'}
                          history={history}
                        />
                        <Feedback
                          value={site.header.feedback}
                          siteId={siteId}
                          siteName={site.name}
                          user={user}
                        />
                      </>
                    );
                  }}
                />
                <Route
                  path="/sites/:siteId/search-results"
                  render={({ location }) => {
                    const queryParams = new URLSearchParams(location.search);
                    const qpPageId = Number(queryParams.get('pageId'));

                    const current =
                      pages.find(page => page.id === qpPageId)?.current ||
                      pages.find(p => p.current)?.current;
                    return (
                      <SearchResultsView
                        site={site}
                        pages={pages}
                        user={user}
                        onUpdateConfig={onUpdateConfig}
                        initiateLogout={initiateLogout}
                        current={current}
                        qpPageId={qpPageId}
                      />
                    );
                  }}
                />
              </SiteEditContext.Provider>
            </ThemeContext.Provider>
          </ThemeProvider>
        </Fragment>
      );
    } else {
      return <Loading message="Loading site" />;
    }
  }
}

export default SiteRouter;
